// Dönem UI saf yardımcıları + TEK kopya kaynağı (Task 10, tasarım sözleşmesi 1A/1B/2A/2B/7A/7B).
// Model: bölüm = TAVAN, soru = TERCİH, etkin dönem = kesişim (shared effectivePeriods).
// Buradaki fonksiyonlar saftır — bileşenler yalnız render eder. E2E spec 13'teki
// (tests/e2e/specs/13-reporting-period.spec.ts) `COPY` bloğu bu metinlerin ELLE
// eşlenik kopyasıdır (Playwright repo kökünden `@mahir/shared`'ı çözemediği için
// import edilemiyor) — buradaki bir kopyayı değiştirince oradakini de güncelle.
import {
  ALL_PERIODS,
  PERIOD_LABELS,
  PERIOD_TYPES,
  effectivePeriods,
  fiscalPeriodRange,
  formatReportingPeriod,
  periodDateRange,
  previousPeriod,
  shortDateRange,
  type PeriodType,
} from "@mahir/shared";

// Rozet/sonuç satırı kısa formu (7B). Yıllık kısaltılmaz — "Yıllık" zaten kısa ve nettir.
export const PERIOD_SHORT_LABELS: Record<PeriodType, string> = {
  annual: "Yıllık",
  q1: "Q1",
  q2: "Q2",
  q3: "Q3",
};

// Tüm dönem kopyaları TEK sabitte. UI'da satır içi string yazılmaz; e2e de buradan okur.
export const PERIOD_COPY = {
  // --- Çeyreklik anahtarı satırı (1A / 2B) ---
  toggleLabel: "Çeyreklik proje oluşturmaya izin ver",
  toggleBadgeOff: "kapalı",
  toggleHelp:
    "Bu çerçeveden 1./2./3. çeyrek projesi açılabilir. Çeyrek = yılbaşından çeyrek sonuna kümülatif ara dönem.",
  noNarrowingInfo:
    'Henüz dönem işaretlemesi yok — tüm sorular her dönemde sorulur. Bölümlerde "Bölümün kapsadığı dönemler"i daraltın.',
  publishedHint: "Düzenlemek için yeni taslak oluşturun.",
  toggleLockedTitle: "Bu çerçeveyi düzenleme yetkiniz yok",
  closingWarning: "{n} çeyreklik proje var. Kapatırsanız yenisi açılamaz; mevcutlar etkilenmez.",
  // --- Anahtar kapalıyken görünmez kısıt (2B / C6) ---
  annualExcludedBadge: "{n} soru yıllık projede sorulmaz",
  annualExcludedLink: "Soruları göster",
  questionAnnualExcluded: "Bu soru yıllık projede sorulmaz (dönem: {periods}). Çeyreklik kapalı.",
  // --- Çip grupları (1B) ---
  sectionLegend: "Bölümün kapsadığı dönemler",
  questionLegend: "Sorunun seçili dönemleri",
  sectionHelp: "Bu bölüm yalnız işaretli dönemlerde sorulur; sorular bu kapsamın dışına çıkamaz.",
  questionHelp: "Bölüm kapsamı içinde kalan işaretler geçerlidir.",
  // --- Sonuç satırı (1B) ---
  resultPrefix: "Sorulur:",
  resultAll: "Tüm dönemlerde sorulur",
  resultNone: "Hiçbir dönemde sorulmaz — bölümü genişletin ya da soruyu daraltın.",
  resultOutside: "{labels} seçili; bölüm kapsamadığı için sorulmaz.",
  sectionScope: "Bölüm {code} kapsamı:",
  // --- Kilit / sınır (2A, R7) ---
  lockedLine: "Yayındaki sürüm düzenlenemez — yeni taslak oluşturun",
  lockedNoPermission: "Bu alanı düzenleme yetkiniz yok.",
  lastChipWarning: "En az bir dönem seçili kalmalı",
  // --- mappingHints (7A) ---
  hintsLabel: "Raporda kullanılan diğer bölüm başlıkları",
  hintsCount: "({n}/30)",
  hintsHelp: "Bu adlar geçmiş rapor başlıklarını bu bölümle eşleştirir.",
  hintsPlaceholder: "Örn. Sürdürülebilirlik Yaklaşımımız",
  hintsAddButton: "Başlık ekle",
  hintsEmpty: "Henüz başlık yok.",
  hintsDuplicate: "Bu başlık zaten ekli.",
  hintsLimit: "En fazla 30 başlık",
  hintsTooLong: "En fazla 120 karakter",
  hintsAdded: "{n} başlık eklendi",
  hintsRemoveAria: "{label} başlığını kaldır",
  // --- Kayıt göstergesi (2A) ---
  saveSaving: "Kaydediliyor…",
  saveSaved: "Kaydedildi ✓",
  saveError: "Kaydedilemedi — yeniden yükle",
  // --- Task 11: ProjectNew dönem seçimi (1C / 6A) ---
  newPeriodLabel: "Raporlama dönemi",
  newPeriodReset: "Dönem Yıllık'a sıfırlandı",
  newPeriodNoQuestions: "{label} için soru tanımlanmamış",
  newPeriodOnlyAnnual: "Bu çerçevede yalnız yıllık rapor sorusu var.",
  newPeriodHelp: "Ara dönem raporları yılbaşından başlar. Yılın tamamı için Yıllık seçin.",
  // M17 T5 — ProjectNew adım 2, yalnız mali yıl müşterisinde (fiscal_year_start_month ≠ 1)
  fiscalNote:
    "Bu müşteri mali yıl kullanıyor: {y} = {start} – {end}. Proje ve form soruları bu yılla etiketlenir; KPI tablosu MY{y} gösterir.",
  // periodDateRange yalnız tarih aralığı döner (R3); kümülatif ibaresi UI kopyasıdır.
  rangeSuffixQ1: "ilk 3 ay (kümülatif)",
  rangeSuffixQ2: "ilk 6 ay (kümülatif)",
  rangeSuffixQ3: "ilk 9 ay (kümülatif)",
  // --- Task 11: ProjectNew özet adımı (1C) ---
  summaryScopePeriod: "{sections} bölüm · {questions} soru (bu dönem)",
  summaryScopeAnnual: "Yıllık: {n} soru",
  summaryScopePackageNote: "(paket hariç tutmaları uygulanmadı)",
  summaryDuplicate: "Bu müşteri için aynı dönemde bir proje zaten var. Devam edebilirsiniz.",
  createPeriodError:
    "Seçilen sektör paketiyle bu dönemde soru kalmıyor. Başka dönem seçin ya da paketi kaldırın.",
  // --- Task 11: liste / arşiv filtresi (2C) ---
  filterLabel: "Dönem (tümü)",
  /** İ4 — Q4 yok (K1: yıllıkla birleşir); filtrede bunu söyler. */
  filterAnnualOption: "Yıllık (4. çeyrek dahil)",
  columnLabel: "Dönem",
  listEmptyPeriod: "Seçilen dönemde proje bulunamadı.",
  listEmptyPeriodHint: "Filtreleri gevşetin veya kaldırın.",
  archiveEmptyHint: "Seçili sektör/yıl/dönem/müşteri filtrelerini temizleyin.",
  clearFilters: "Filtreleri temizle",
  // --- Task 11: FormFill üst bar (3A) ---
  formPeriodLine: "{label} = {range} (kümülatif ara dönem)",
  // --- Task 11: önceki dönem önerisi (2D) ---
  // Yıllık metinler AYNEN korunur — e2e spec 11 regex'i bunlara bağlı.
  priorAnnualTitle: "Önceki yıl ({year}) yanıtı",
  priorAnnualCopy: "Bu yıl aynı",
  priorPeriodTitle: "Geçen yılın aynı dönemi ({label}) yanıtı",
  priorPeriodCopy: "Bu dönem aynı",
  priorPeriodMissing: "Geçen yılın aynı dönemi ({label}) projesi yok — öneri gösterilemiyor.",
  // --- G8 (Geçmiş Rapor Aktarımı, Task 12): köken bandı varken PriorYearHint tek satıra
  // daralır; bu satırın metni. IMPORT_COPY'de DEĞİL — genel dönem kopyasına ait (import'a
  // özgü değil, her PriorYearHint için geçerli).
  priorCollapsedAnnual: "Geçen yılın yanıtı",
  // --- Finar rev-B 19: uzun öneri metni ilk 300 karakter + satır içi aç/kapa ---
  priorShowMore: "devamını gör",
  priorShowLess: "daha az göster",
  // --- Task 11: branding token çipi ---
  brandingTokenLabel: "Dönem",
} as const;

// "{n} soru…" gibi yer tutucuları doldurur. Eksik anahtar dokunulmadan kalır (sessiz bozulma yok).
export function fillTemplate(template: string, vars: Record<string, string | number>): string {
  let out = template;
  for (const [key, value] of Object.entries(vars)) {
    out = out.split(`{${key}}`).join(String(value));
  }
  return out;
}

// Çip toggle — kanonik sıra korunur, son seçili dönem çıkarılamaz (liste aynen döner).
export function togglePeriod(current: readonly PeriodType[], period: PeriodType): PeriodType[] {
  const selected = new Set(current);
  if (selected.has(period)) {
    if (selected.size === 1) return [...current];
    selected.delete(period);
  } else {
    selected.add(period);
  }
  return PERIOD_TYPES.filter((p) => selected.has(p));
}

// Kısa rozet: "Yıllık · Q1". Boş küme görsel olarak tire.
export function formatPeriodBadge(periods: readonly PeriodType[]): string {
  const ordered = PERIOD_TYPES.filter((p) => periods.includes(p));
  if (ordered.length === 0) return "—";
  return ordered.map((p) => PERIOD_SHORT_LABELS[p]).join(" · ");
}

export interface PeriodResultText {
  text: string;
  tone: "info" | "warning";
}

// Soru çiplerinin altındaki kalıcı sonuç satırının ANA cümlesi (1B).
// R8: yalnız yıllıkta da düz rozet kullanılır → "Sorulur: Yıllık".
export function describeEffectivePeriods(effective: readonly PeriodType[]): PeriodResultText {
  if (effective.length === 0) return { text: PERIOD_COPY.resultNone, tone: "warning" };
  if (effective.length === ALL_PERIODS.length) return { text: PERIOD_COPY.resultAll, tone: "info" };
  return { text: `${PERIOD_COPY.resultPrefix} ${formatPeriodBadge(effective)}`, tone: "info" };
}

// Soruda seçili ama bölüm tavanının dışında kalan dönemler (üstü çizili çip YOK — açıklama satırı).
export function outsideCeiling(
  ceiling: readonly PeriodType[],
  selected: readonly PeriodType[],
): PeriodType[] {
  return PERIOD_TYPES.filter((p) => selected.includes(p) && !ceiling.includes(p));
}

export function formatOutsideNote(outside: readonly PeriodType[]): string | null {
  if (outside.length === 0) return null;
  const labels = PERIOD_TYPES.filter((p) => outside.includes(p))
    .map((p) => PERIOD_LABELS[p])
    .join(" · ");
  return fillTemplate(PERIOD_COPY.resultOutside, { labels });
}

// Ağaç türetmeleri — yapısal tip: SectionWithQuestions bunlara atanabilir.
export interface PeriodQuestionLike {
  id: string;
  applicablePeriods: readonly PeriodType[];
}
export interface PeriodSectionLike {
  id: string;
  applicablePeriods: readonly PeriodType[];
  questions: readonly PeriodQuestionLike[];
}

export interface AnnualExcludedEntry {
  sectionId: string;
  questionId: string;
  periods: PeriodType[];
}

// 2B: anahtar KAPALIYKEN "hiç sorulmayacak" sorular — etkin kesişiminde annual olmayanlar.
// N server'dan değil ağaçtan türetilir (optimistic çip yazımıyla anında güncel kalsın).
export function annualExcludedQuestions(
  sections: readonly PeriodSectionLike[],
): AnnualExcludedEntry[] {
  const out: AnnualExcludedEntry[] = [];
  for (const section of sections) {
    for (const question of section.questions) {
      // Kesişim TEK yerde: shared effectivePeriods (fix round 1 / bulgu 6).
      const periods = effectivePeriods(section.applicablePeriods, question.applicablePeriods);
      if (!periods.includes("annual")) {
        out.push({ sectionId: section.id, questionId: question.id, periods });
      }
    }
  }
  return out;
}

// --- mappingHints giriş mantığı (7A) — saf, bileşen yalnız render eder ---
export const MAPPING_HINT_MAX = 30;
export const MAPPING_HINT_MAX_LEN = 120;

export interface PreparedHints {
  accepted: string[];
  // Mevcut listede (ya da aynı yapıştırmada) zaten olan başlık atlandı.
  duplicate: boolean;
  // 120 karakteri aşan başlık kesildi.
  truncated: boolean;
  // 30 sınırı doldu, kalanlar atıldı.
  limitReached: boolean;
}

// Virgül/satır sonu ile bölünmüş girdiyi mevcut listeye göre hazırlar.
// Karşılaştırma Türkçe küçük harfe duyarsız (İstanbul/istanbul aynı başlıktır).
export function prepareMappingHints(existing: readonly string[], raw: string): PreparedHints {
  const seen = new Set(existing.map((h) => h.trim().toLocaleLowerCase("tr")));
  const out: PreparedHints = {
    accepted: [],
    duplicate: false,
    truncated: false,
    limitReached: false,
  };
  for (const piece of raw.split(/[,\n\r]+/)) {
    const trimmed = piece.trim();
    if (trimmed.length === 0) continue;
    let value = trimmed;
    if (value.length > MAPPING_HINT_MAX_LEN) {
      value = value.slice(0, MAPPING_HINT_MAX_LEN).trim();
      out.truncated = true;
    }
    const key = value.toLocaleLowerCase("tr");
    if (seen.has(key)) {
      out.duplicate = true;
      continue;
    }
    if (existing.length + out.accepted.length >= MAPPING_HINT_MAX) {
      out.limitReached = true;
      break;
    }
    seen.add(key);
    out.accepted.push(value);
  }
  return out;
}

// 1A: ağaçta hiç daraltma yoksa bilgi satırı gösterilir.
export function hasNoPeriodNarrowing(sections: readonly PeriodSectionLike[]): boolean {
  return sections.every(
    (s) =>
      s.applicablePeriods.length === ALL_PERIODS.length &&
      s.questions.every((q) => q.applicablePeriods.length === ALL_PERIODS.length),
  );
}

// --- Task 11: tüketici ekranları (ProjectNew / liste / FormFill / prior-year) ---

/** PublishedFrameworkEntry.periodQuestionCounts ile aynı şekil (dört anahtar zorunlu). */
export type PeriodQuestionCounts = Record<PeriodType, number>;

export interface PeriodOption {
  period: PeriodType;
  label: string;
  questionCount: number;
  /** Bu dönemde hiç soru yok → seçilemez. Yıllık ASLA disabled olmaz: geri
   *  düşülecek bir dönem her zaman kalmalı (backend de yalnız qN'i 0 soruda reddeder). */
  disabled: boolean;
  /** 0 soru durumunda segmentin altında görünen açıklama satırı. */
  note: string | null;
}

export function buildPeriodOptions(counts: PeriodQuestionCounts): PeriodOption[] {
  return PERIOD_TYPES.map((period) => {
    const questionCount = counts[period];
    const empty = questionCount === 0;
    return {
      period,
      label: PERIOD_LABELS[period],
      questionCount,
      disabled: empty && period !== "annual",
      note: empty
        ? fillTemplate(PERIOD_COPY.newPeriodNoQuestions, { label: PERIOD_LABELS[period] })
        : null,
    };
  });
}

/** Üç çeyrekten en az birinde soru varsa segment gösterilir; yoksa tamamen gizlenir. */
export function hasQuarterlyQuestions(counts: PeriodQuestionCounts): boolean {
  return counts.q1 > 0 || counts.q2 > 0 || counts.q3 > 0;
}

const RANGE_SUFFIX: Record<PeriodType, string | null> = {
  annual: null,
  q1: PERIOD_COPY.rangeSuffixQ1,
  q2: PERIOD_COPY.rangeSuffixQ2,
  q3: PERIOD_COPY.rangeSuffixQ3,
};

/** Segment altındaki dinamik tarih satırı: "1 Ocak – 30 Haziran 2025 · ilk 6 ay (kümülatif)". */
export function periodRangeLine(year: number, period: PeriodType): string {
  const { label } = periodDateRange(year, period);
  const suffix = RANGE_SUFFIX[period];
  return suffix === null ? label : `${label} · ${suffix}`;
}

/** M17 T5 — mali yıl notu; takvim müşterisinde (1 / bilinmiyor) null → adım 2 byte-eşit. */
export function fiscalYearNote(year: number, startMonth: number | undefined): string | null {
  if (startMonth === undefined || startMonth === 1) return null;
  const r = fiscalPeriodRange(year, startMonth, "annual");
  const [start = "", end = ""] = shortDateRange(r.start, r.end).split(" – ");
  return fillTemplate(PERIOD_COPY.fiscalNote, { y: year, start, end });
}

/** FormFill üst barındaki açıklama satırı — yıllıkta satır YOK (null). */
export function formPeriodDateLine(year: number, period: PeriodType): string | null {
  if (period === "annual") return null;
  return fillTemplate(PERIOD_COPY.formPeriodLine, {
    label: PERIOD_LABELS[period],
    range: periodDateRange(year, period).label,
  });
}

export interface PriorYearHintCopy {
  title: string;
  copyLabel: string;
  /**
   * G8 (Geçmiş Rapor Aktarımı, Task 12) — köken/provenance bandı gösterildiğinde
   * PriorYearHint tek satıra daralır (`collapsed` prop); bu satırın linki. Yıllıkta
   * `title`'dan FARKLI (yıl numarası taşımaz, kısa jenerik link); çeyreklikte `title`
   * ile AYNI metin (zaten kısa). `title`/`copyLabel` byte-eşit korunur (spec 11 regex'i).
   */
  collapsedLabel: string;
}

/** Önceki dönem önerisi başlığı + kopyala düğmesi. Yıllık metinler AYNEN korunur (spec 11). */
export function priorYearHintCopy(year: number, period: PeriodType): PriorYearHintCopy {
  if (period === "annual") {
    return {
      title: fillTemplate(PERIOD_COPY.priorAnnualTitle, { year }),
      copyLabel: PERIOD_COPY.priorAnnualCopy,
      collapsedLabel: PERIOD_COPY.priorCollapsedAnnual,
    };
  }
  const title = fillTemplate(PERIOD_COPY.priorPeriodTitle, {
    label: formatReportingPeriod(year, period, { short: true }),
  });
  return {
    title,
    copyLabel: PERIOD_COPY.priorPeriodCopy,
    collapsedLabel: title,
  };
}

/**
 * Çeyreklik projede hiç öneri yoksa formun üstünde görünen satır. Girdi MEVCUT
 * projenin yılı/dönemi; metin bir önceki yılın aynı dönemini adlandırır.
 * Yıllık projede satır YOK (null) — mevcut davranış değişmez.
 */
export function priorPeriodMissingNote(year: number, period: PeriodType): string | null {
  if (period === "annual") return null;
  const prev = previousPeriod(year, period);
  return fillTemplate(PERIOD_COPY.priorPeriodMissing, {
    label: formatReportingPeriod(prev.year, prev.type, { short: true }),
  });
}

/** İ4 — liste/arşiv dönem süzgeci seçenekleri; yıllık "4. çeyrek dahil" ipucunu taşır. */
export function periodFilterOptions(): { value: PeriodType; label: string }[] {
  return PERIOD_TYPES.map((p) => ({
    value: p,
    label: p === "annual" ? PERIOD_COPY.filterAnnualOption : PERIOD_LABELS[p],
  }));
}

/**
 * İ4 — liste yıl süzgeci yalnız VERİDE olan yılları gösterir (sunucu `/projects/filters`
 * + sayfadaki kayıtlar); takvim penceresi (now-4…now+3) kalktı. Yeni yıldan büyüğe sıralı.
 */
export function yearFilterOptions(
  serverYears: readonly number[],
  pageYears: readonly number[],
): { value: string; label: string }[] {
  const years = new Set<number>([...serverYears, ...pageYears]);
  return [...years].sort((a, b) => b - a).map((y) => ({ value: String(y), label: String(y) }));
}
